import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { apiFundo, type Caso, type FundoDoCaso } from '../lib/api.ts';
import { maiuscula } from '../lib/caixa.ts';
import { paraTela } from '../lib/datas.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { completarReais, digitarReais, mascararDinheiro, paraApi } from '../lib/dinheiro.ts';
import { avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso } from './PassoAPasso.tsx';
import { Valor } from './Valor.tsx';
import '../estilos/dinheiro-2026.css';

/**
 * O Fundo de Custas de um caso — pedido do Richard, 15/08/2026.
 *
 * Dinheiro **do cliente**, adiantado para pagar documento de terceiro. Aqui se
 * lança o que entrou e o que saiu, e daqui sai a prestação de contas em PDF —
 * durante o trabalho, se o cliente pedir, e no fim, junto da entrega.
 *
 * ⚠️ **Não é receita da KAIRÓS.** O saldo volta ao cliente. Por isso ele não
 * entra em soma nenhuma do Financeiro, e por isso a tela fala em "devolver",
 * nunca em "sobra".
 *
 * ⭐ **No desenho novo desde 18/09/2026** (`docs/FORMULARIOS-E-LINK-DO-
 * CLIENTE.md`, A.4): os três números no alto, e uma aba por assunto — o que foi
 * pago, os depósitos, o teto, a devolução. ⛔ Cada lançamento continua gravando
 * na hora, como antes; e **apagar e desfazer passaram a perguntar** (a regra
 * dele, 18/09/2026: todo apagar tem a janelinha).
 */
type Aba = 'gastos' | 'depositos' | 'teto' | 'devolucao';
const ABAS: Array<{ chave: Aba; rotulo: string }> = [
  { chave: 'gastos', rotulo: 'O que foi pago' },
  { chave: 'depositos', rotulo: 'Depósitos' },
  { chave: 'teto', rotulo: 'Teto' },
  { chave: 'devolucao', rotulo: 'Devolução' },
];

type AApagar =
  | { tipo: 'gasto'; id: string; texto: string }
  | { tipo: 'deposito'; id: string; texto: string }
  | { tipo: 'devolucao'; texto: string };

export function FundoDeCustas({ caso, aoFechar, aoMudar }: { caso: Caso; aoFechar: () => void; aoMudar: () => void }) {
  const [fundo, setFundo] = useState<FundoDoCaso | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [aba, setAba] = useState<Aba>('gastos');
  const [aApagar, setAApagar] = useState<AApagar | null>(null);

  const [teto, setTeto] = useState('');
  const [dep, setDep] = useState({ valor: '', data: '', observacao: '' });
  const [gasto, setGasto] = useState({ descricao: '', valor: '', data: '' });
  const [devolucao, setDevolucao] = useState({ data: '', valor: '' });

  usarDialogo(aoFechar);

  const carregar = useCallback(async () => {
    try {
      const f = await apiFundo.ler(caso.id);
      setFundo(f);
      setTeto(f.resumo.teto ? mascararDinheiro(f.resumo.teto.replace('.', '')) : '');
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui carregar o fundo.');
    }
  }, [caso.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Todo lançamento passa por aqui: uma tratativa de erro, não seis. */
  async function agir(o: () => Promise<FundoDoCaso>): Promise<boolean> {
    if (salvando) return false;
    setSalvando(true);
    setErro(null);
    try {
      setFundo(await o());
      aoMudar();
      return true;
    } catch (e) {
      avisarErro(e);
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      return false;
    } finally {
      setSalvando(false);
    }
  }

  function apagar(x: AApagar) {
    if (x.tipo === 'gasto') void agir(() => apiFundo.apagarGasto(caso.id, x.id));
    else if (x.tipo === 'deposito') void agir(() => apiFundo.apagarDeposito(caso.id, x.id));
    else void agir(() => apiFundo.definir(caso.id, { devolvidoEm: null, devolvido: null }));
  }

  const r = fundo?.resumo;
  const contagem: Record<Aba, number | null> = { gastos: fundo?.gastos.length ?? null, depositos: fundo?.depositos.length ?? null, teto: null, devolucao: null };

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="imv cad-conferir din-ficha" role="dialog" aria-modal="true" aria-label="Fundo de Custas">
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">
                Caso <span className="din-codigo">{caso.codigo}</span> · fundo de custas
              </span>
              <h3 className="cad-titulo">Fundo de Custas</h3>
              <p className="cad-sub">
                {caso.titulo ? `${caso.titulo} · ` : ''}
                {caso.cliente.nome} · dinheiro do cliente, adiantado para documento de terceiro. O saldo volta a ele.
              </p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={aoFechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>

          {!fundo || !r ? (
            <p className="imv-nada din-carregando">{erro ?? 'Carregando…'}</p>
          ) : (
            <>
              <div className="din-numeros">
                <div className="imv-kpi">
                  <span className="imv-rot">Depositado</span>
                  <strong>
                    <Valor de={r.depositado} />
                  </strong>
                  <small>{fundo.depositos.length === 1 ? '1 depósito' : `${fundo.depositos.length} depósitos`}</small>
                </div>
                <div className={`imv-kpi${r.estourou ? ' din-kpi-alerta' : r.perto ? ' k-sem' : ''}`}>
                  <span className="imv-rot">Gasto</span>
                  <strong>
                    <Valor de={r.gasto} />
                  </strong>
                  <small>
                    {r.teto ? (
                      <>
                        de <Valor de={r.teto} /> de teto
                        {r.consumidoDoTeto !== null && ` · ${Math.round(r.consumidoDoTeto * 100)}%`}
                      </>
                    ) : (
                      'sem teto combinado'
                    )}
                  </small>
                  {r.teto && r.consumidoDoTeto !== null && (
                    <span className="din-barra" aria-hidden="true">
                      <i style={{ width: `${Math.min(100, Math.round(r.consumidoDoTeto * 100))}%` }} />
                    </span>
                  )}
                </div>
                <div className={`imv-kpi${r.devolvidoEm ? ' k-fora' : ' k-livre'}`}>
                  <span className="imv-rot">{r.devolvidoEm ? 'Devolvido' : 'A devolver ao cliente'}</span>
                  <strong>
                    <Valor de={r.devolvidoEm && r.devolvido ? r.devolvido : r.saldo} />
                  </strong>
                  <small>{r.devolvidoEm ? `em ${paraTela(r.devolvidoEm)}` : 'o que foi depositado menos o que foi pago'}</small>
                </div>
              </div>

              {fundo.avisos.map((a) => (
                <p key={a} className={r.estourou ? 'erro imv-erro din-aviso-topo' : 'imv-pendencias din-aviso-topo'}>
                  {a}
                </p>
              ))}

              <div className="din-abas imv-seg" role="tablist" aria-label="Assunto do fundo">
                {ABAS.map((x) => (
                  <button key={x.chave} type="button" role="tab" aria-selected={aba === x.chave} className={`imv-op neutro${aba === x.chave ? ' on' : ''}`} onClick={() => setAba(x.chave)}>
                    <i />
                    {x.rotulo}
                    {contagem[x.chave] !== null && <span className="din-conta">{contagem[x.chave]}</span>}
                  </button>
                ))}
              </div>

              <div className="din-painel" role="tabpanel">
                {aba === 'gastos' && (
                  <>
                    <form
                      className="din-lancar"
                      onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                        if (!gasto.descricao.trim() || !paraApi(gasto.valor) || !gasto.data) return;
                        void agir(() => apiFundo.gastar(caso.id, { descricao: gasto.descricao.trim(), valor: paraApi(gasto.valor), data: gasto.data })).then(
                          (ok) => ok && setGasto({ descricao: '', valor: '', data: '' }),
                        );
                      }}
                    >
                      <div className="imv-grade-campos">
                        {/* A descrição pede o dobro do espaço: é frase, e as outras duas são número e data. */}
                        <CampoDoPasso rotulo="O que foi pago" col={6} nota="Vira uma linha da prestação de contas: escreva o que o cliente precisa ler para reconhecer o gasto.">
                          <input id="gasto-desc" value={gasto.descricao} onChange={(e) => setGasto({ ...gasto, descricao: maiuscula(e.target.value) })} placeholder="Ex.: MATRÍCULA DE IMÓVEL — 3º CRI DE SÃO PAULO" />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="Valor" col={2} mono>
                          <i className="imv-pre">R$</i>
                          <input id="gasto-valor" inputMode="decimal" value={gasto.valor} onChange={(e) => setGasto({ ...gasto, valor: digitarReais(e.target.value) })} onBlur={(e) => setGasto({ ...gasto, valor: completarReais(e.target.value) })} placeholder="0,00" />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="Data do pagamento" col={2} mono>
                          <input id="gasto-data" type="date" value={gasto.data} onChange={(e) => setGasto({ ...gasto, data: e.target.value })} />
                        </CampoDoPasso>
                        <div className="imv-campo c2 din-botao-da-linha">
                          <button type="submit" className="imv-bt primario" disabled={salvando || !gasto.descricao.trim() || !gasto.valor || !gasto.data}>
                            <Icone nome="mais" tamanho={14} /> Lançar gasto
                          </button>
                        </div>
                      </div>
                    </form>
                    <Lista
                      vazia="Nenhum documento pago ainda."
                      linhas={fundo.gastos.map((g) => ({ id: g.id, data: g.data, texto: g.descricao, valor: g.valor }))}
                      aoApagar={(l) => setAApagar({ tipo: 'gasto', id: l.id, texto: `${l.texto} (${paraTela(l.data)})` })}
                    />
                  </>
                )}

                {aba === 'depositos' && (
                  <>
                    <form
                      className="din-lancar"
                      onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                        if (!paraApi(dep.valor) || !dep.data) return;
                        void agir(() => apiFundo.depositar(caso.id, { valor: paraApi(dep.valor), data: dep.data, observacao: dep.observacao.trim() || null })).then(
                          (ok) => ok && setDep({ valor: '', data: '', observacao: '' }),
                        );
                      }}
                    >
                      <div className="imv-grade-campos">
                        <CampoDoPasso rotulo="Valor" col={2} mono>
                          <i className="imv-pre">R$</i>
                          <input id="dep-valor" inputMode="decimal" value={dep.valor} onChange={(e) => setDep({ ...dep, valor: digitarReais(e.target.value) })} onBlur={(e) => setDep({ ...dep, valor: completarReais(e.target.value) })} placeholder="0,00" />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="Data" col={2} mono>
                          <input id="dep-data" type="date" value={dep.data} onChange={(e) => setDep({ ...dep, data: e.target.value })} />
                        </CampoDoPasso>
                        <div className="imv-campo c2 din-botao-da-linha">
                          <button type="submit" className="imv-bt primario" disabled={salvando || !dep.valor || !dep.data}>
                            <Icone nome="mais" tamanho={14} /> Lançar depósito
                          </button>
                        </div>
                        <CampoDoPasso rotulo="Observação" col={6}>
                          <input id="dep-obs" value={dep.observacao} onChange={(e) => setDep({ ...dep, observacao: e.target.value })} placeholder="Opcional. Ex.: depósito complementar autorizado" />
                        </CampoDoPasso>
                      </div>
                    </form>
                    <Lista
                      vazia="Nenhum depósito lançado."
                      linhas={fundo.depositos.map((d) => ({ id: d.id, data: d.data, texto: d.observacao ?? 'Depósito no Fundo de Custas', valor: d.valor }))}
                      aoApagar={(l) => setAApagar({ tipo: 'deposito', id: l.id, texto: `o depósito de ${paraTela(l.data)}` })}
                    />
                  </>
                )}

                {aba === 'teto' && (
                  <form
                    className="din-lancar"
                    onSubmit={(e: FormEvent) => {
                      e.preventDefault();
                      void agir(() => apiFundo.definir(caso.id, { teto: paraApi(teto) || null }));
                    }}
                  >
                    <div className="imv-grade-campos">
                      <CampoDoPasso rotulo="Teto acordado" col={3} mono nota="É o valor de cláusula. Aos 80% dele o Hub avisa vocês; aos 90% a proposta manda comunicar o cliente, com novo depósito combinado.">
                        <i className="imv-pre">R$</i>
                        <input id="fundo-teto" inputMode="decimal" value={teto} onChange={(e) => setTeto(digitarReais(e.target.value))} onBlur={(e) => setTeto(completarReais(e.target.value))} placeholder="0,00" />
                      </CampoDoPasso>
                      <div className="imv-campo c2 din-botao-da-linha">
                        <button type="submit" className="imv-bt" disabled={salvando}>
                          <Icone nome="check" tamanho={14} /> Guardar teto
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {aba === 'devolucao' &&
                  (r.devolvidoEm ? (
                    <div className="din-devolvido">
                      <p>
                        <b>Devolvido em {paraTela(r.devolvidoEm)}</b>
                        {r.devolvido && (
                          <>
                            {' · '}
                            <Valor de={r.devolvido} />
                          </>
                        )}
                        .
                      </p>
                      <button
                        type="button"
                        className="imv-bt fantasma"
                        onClick={() => setAApagar({ tipo: 'devolucao', texto: `a devolução de ${paraTela(r.devolvidoEm!)}` })}
                      >
                        Desfazer
                      </button>
                    </div>
                  ) : (
                    <form
                      className="din-lancar"
                      onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                        if (!devolucao.data) return;
                        void agir(() => apiFundo.definir(caso.id, { devolvidoEm: devolucao.data, devolvido: paraApi(devolucao.valor) || r.saldo }));
                      }}
                    >
                      <div className="imv-grade-campos">
                        <CampoDoPasso rotulo="Devolvido em" col={2} mono>
                          <input id="dev-data" type="date" value={devolucao.data} onChange={(e) => setDevolucao({ ...devolucao, data: e.target.value })} />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="Valor" col={2} mono nota="Em branco, vale o saldo.">
                          <i className="imv-pre">R$</i>
                          <input id="dev-valor" inputMode="decimal" value={devolucao.valor} onChange={(e) => setDevolucao({ ...devolucao, valor: digitarReais(e.target.value) })} onBlur={(e) => setDevolucao({ ...devolucao, valor: completarReais(e.target.value) })} placeholder="0,00" />
                        </CampoDoPasso>
                        <div className="imv-campo c2 din-botao-da-linha">
                          <button type="submit" className="imv-bt" disabled={salvando || !devolucao.data}>
                            <Icone nome="check" tamanho={14} /> Registrar devolução
                          </button>
                        </div>
                      </div>
                    </form>
                  ))}
              </div>

              {erro && <p className="erro imv-erro">{erro}</p>}
            </>
          )}

          <footer className="imv-ficha-pe">
            <span className="din-grava-na-hora">Cada lançamento grava na hora.</span>
            <span className="imv-esp" />
            {/* Aba nova: o navegador mostra o PDF e de lá se imprime ou salva.
                Baixar direto tiraria a chance de conferir antes de enviar. */}
            <a className="imv-bt" href={apiFundo.prestacaoDeContas(caso.id)} target="_blank" rel="noopener">
              <Icone nome="baixar" tamanho={14} /> Prestação de contas (PDF)
            </a>
            <button type="button" className="imv-bt primario" onClick={aoFechar}>
              Fechar
            </button>
          </footer>
        </section>
      </div>

      {aApagar && (
        <Confirmar
          titulo={aApagar.tipo === 'devolucao' ? 'Desfazer a devolução?' : aApagar.tipo === 'gasto' ? 'Apagar este gasto?' : 'Apagar este depósito?'}
          descricao={
            aApagar.tipo === 'devolucao'
              ? `O registro de ${aApagar.texto} sai, e o saldo volta a aparecer como a devolver.`
              : `${aApagar.texto.charAt(0).toUpperCase()}${aApagar.texto.slice(1)} sai do fundo e da prestação de contas, e não volta.`
          }
          rotuloAcao={aApagar.tipo === 'devolucao' ? 'Desfazer' : 'Apagar'}
          aoFechar={() => setAApagar(null)}
          aoConfirmar={() => apagar(aApagar)}
        />
      )}
    </div>
  );
}

function Lista({
  vazia,
  linhas,
  aoApagar,
}: {
  vazia: string;
  linhas: Array<{ id: string; data: string; texto: string; valor: string }>;
  aoApagar: (l: { id: string; data: string; texto: string }) => void;
}) {
  if (linhas.length === 0) return <p className="imv-nada din-vazia">{vazia}</p>;
  return (
    <div className="imv-tabela din-tabela din-lista">
      {linhas.map((l) => (
        <div key={l.id} className="imv-tl din-grade-lista">
          <span className="mono">{paraTela(l.data)}</span>
          <span>{l.texto}</span>
          <span className="mono din-valor">
            <Valor de={l.valor} />
          </span>
          <button type="button" className="imv-x" aria-label={`Apagar: ${l.texto}`} title="Apagar" onClick={() => aoApagar(l)}>
            <Icone nome="lixeira" tamanho={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
